import { useState } from 'react';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import { EntityImage } from '@urbanx/agx-ui-components';
import type { MonthEndEntry, MonthEndReport } from '@urbanx/agx.rex.client';
import {
  AgxButton,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  dateOnlyToDate,
  toInclusiveDate,
} from '@urbanx/agx.ui-components2';
import { monthEndAwardLabels, monthEndCurrency, monthEndEmail } from './monthEndEmail';
import * as styles from './MonthEndReport.css';

const Leaderboard = ({
  title,
  report,
  entries,
  agency,
}: {
  title: string;
  report: MonthEndReport;
  entries: MonthEndEntry[];
  agency?: boolean;
}) => (
  <section aria-label={title} className={styles.leaderboard}>
    <AgxTypography type="title" size="small">
      {title} -{' '}
      {dateOnlyToDate(report.fromDate)?.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })}{' '}
      -{' '}
      {dateOnlyToDate(toInclusiveDate(report.toDateExclusive))?.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })}
    </AgxTypography>
    <AgxTable
      tableStyle="rounded"
      bodyBg={tokens.bg.secondary}
      headerBg={tokens.bg.secondary}
      columns={[agency ? 'Agency' : 'Agent', { name: 'GCI', align: 'right' }]}
      rows={entries.map((entry) => ({
        [agency ? 'Agency' : 'Agent']: (
          <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="center">
            <span className={styles.place}>{entry.place}</span>
            <EntityImage
              name={entry.name}
              photoUri={entry.photoUri}
              backgroundColour={entry.backgroundColour}
              backgroundTextColour={entry.backgroundTextColour}
              addRingToImage={!agency}
              size={32}
            />
            <span>{entry.name}</span>
          </AgxSpacer>
        ),
        GCI: monthEndCurrency(entry.value),
      }))}
      noDataLabel="No qualifying winner"
    />
  </section>
);

export const MonthEndSummary = ({ report }: { report: MonthEndReport }) => {
  const [result, setResult] = useState<{
    report: MonthEndReport;
    error: boolean;
    message: string;
  }>();
  const [copying, setCopying] = useState(false);
  const copy = async () => {
    setCopying(true);
    setResult(undefined);
    try {
      const content = monthEndEmail(report);
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([content.html], { type: 'text/html' }),
            'text/plain': new Blob([content.text], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(content.text);
      }
      setResult({
        report,
        error: false,
        message: 'Email summary copied. Paste it into your email.',
      });
    } catch {
      setResult({
        report,
        error: true,
        message:
          'Unable to copy. Allow clipboard access and try again, or select and copy the report below.',
      });
    } finally {
      setCopying(false);
    }
  };
  return (
    <AgxSpacer direction="vertical" gap="spacing-2xl" fill>
      <AgxSpacer direction="horizontal" gap="spacing-xl" wrap contentAlignment="center">
        <AgxButton
          id="month-end-copy-email"
          text="Copy email summary"
          variant="hollow"
          disabled={copying}
          onClick={() => void copy()}
        />
      </AgxSpacer>
      <AgxTypography type="body" size="small">
        Copy the formatted summary into an email with whole-dollar amounts and unconditional network
        totals.
      </AgxTypography>
      {result?.report === report && (
        <p role={result.error ? 'alert' : 'status'}>{result.message}</p>
      )}
      <Entries
        title="#1 Agent (GCI)"
        entries={report.individualTeamGciRankings.filter((entry) => entry.place === 1)}
      />
      <Entries
        title="#1 Agency (GCI)"
        entries={report.agencyGciRankings.filter((entry) => entry.place === 1)}
      />
      {report.awards.map((award) => (
        <Entries
          key={award.name}
          title={monthEndAwardLabels[award.name] ?? award.name}
          entries={award.winners}
          unit={award.unit}
        />
      ))}
      <section aria-label="Network totals">
        <AgxTypography type="title" size="medium">
          Network totals
        </AgxTypography>
        <AgxTable
          columns={[
            'Basis',
            { name: 'Network Total Sales Volume', align: 'right' },
            { name: 'Network Total GCI', align: 'right' },
          ]}
          rows={report.networkTotals.map((total) => ({
            Basis: total.basis,
            'Network Total Sales Volume': monthEndCurrency(total.salesVolume),
            'Network Total GCI': monthEndCurrency(total.gci),
          }))}
        />
      </section>
      <Leaderboard
        title="Top 10 Agents (GCI)"
        report={report}
        entries={report.individualTeamGciRankings}
      />
      <Leaderboard
        title="Top 10 Agencies (GCI)"
        agency
        report={report}
        entries={report.agencyGciRankings}
      />
    </AgxSpacer>
  );
};

const Entries = ({
  title,
  entries,
  unit = 'Currency',
}: {
  title: string;
  entries: MonthEndEntry[];
  unit?: string;
}) => (
  <section aria-label={title}>
    <strong>{title}</strong>
    {entries.length ? (
      entries.map((entry) => (
        <div key={entry.id}>
          <span>{entry.name}</span>
          {entry.sellingAgents?.length ? ` — ${entry.sellingAgents.join('; ')}` : ''}
          {' – '}
          {unit === 'Currency'
            ? monthEndCurrency(entry.value)
            : entry.value.toLocaleString('en-AU')}
        </div>
      ))
    ) : (
      <div>No qualifying winner</div>
    )}
  </section>
);
